import PropTypes from 'prop-types';
import React from 'react';
import cn from 'classnames';

import { accessor } from '../../utils/propTypes';
import EventWrapper from './EventWrapper';
import EventContainerWrapper from './EventContainerWrapper';
import WeekWrapper from './WeekWrapper';
import { mergeComponents } from './common';

/**
 * Creates a higher-order component (HOC) supporting drag & drop and optionally resizing
 * of events:
 *
 * ```js
 *    import Calendar from 'react-big-calendar'
 *    import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
 *    export default withDragAndDrop(Calendar)
 * ```
 * (you can optionally pass any dnd backend as an optional second argument to `withDragAndDrop`.
 * It defaults to `react-dnd-html5-backend` which you should probably include in
 * your project if using this default).
 *
 * Set `resizable` to true in your calendar if you want events to be resizable.
 *
 * The HOC adds `onEventDrop` and `onEventResize` callback properties if the events are
 * moved or resized. They are called with these signatures:
 *
 * ```js
 *    function onEventDrop({ event, start, end, allDay }) {...}
 *    function onEventResize(type, { event, start, end, allDay }) {...}  // type is always 'drop'
 * ```
 *
 * Moving and resizing of events has some subtlety which one should be aware of.
 *
 * In some situations, non-allDay events are displayed in "row" format where they
 * are rendered horizontally. This is the case for ALL events in a month view. It
 * is also occurs with multi-day events in a day or week view (unless `showMultiDayTimes`
 * is set).
 *
 * When dropping or resizing non-allDay events into a the header area or when
 * resizing them horizontally because they are displayed in row format, their
 * times are preserved, only their date is changed.
 *
 * If you care about these corner cases, you can examine the `allDay` param suppled
 * in the callback to determine how the user dropped or resized the event.
 *
 * @param {*} Calendar
 * @param {*} backend
 */
export default function withDragAndDrop(Calendar) {
    class DragAndDropCalendar extends React.Component {
        static propTypes = {
            onEventDrop: PropTypes.func,
            onEventResize: PropTypes.func,

            draggableAccessor: accessor,
            resizableAccessor: accessor,

            selectable: PropTypes.oneOf([true, false, 'ignoreEvents']),
            resizable: PropTypes.bool,
            components: PropTypes.object,
            step: PropTypes.number,
        };

        // eslint-disable-next-line react/sort-comp
        static defaultProps = {
            // TODO: pick these up from Calendar.defaultProps
            components: {},
            draggableAccessor: null,
            resizableAccessor: null,
            step: 30,
        };

        static contextTypes = {
            dragDropManager: PropTypes.object,
        };

        static childContextTypes = {
            draggable: PropTypes.shape({
                onStart: PropTypes.func,
                onEnd: PropTypes.func,
                onBeginAction: PropTypes.func,
                draggableAccessor: accessor,
                resizableAccessor: accessor,
                // eslint-disable-next-line react/forbid-prop-types
                dragAndDropAction: PropTypes.object,
            }),
        };

        constructor(...args) {
            super(...args);

            // eslint-disable-next-line react/prop-types
            const { components } = this.props;

            this.components = mergeComponents(components, {
                eventWrapper: EventWrapper,
                eventContainerWrapper: EventContainerWrapper,
                weekWrapper: WeekWrapper,
            });

            this.state = { interacting: false };
        }

        getChildContext() {
            return {
                draggable: {
                    onStart: this.handleInteractionStart,
                    onEnd: this.handleInteractionEnd,
                    onBeginAction: this.handleBeginAction,
                    // eslint-disable-next-line react/prop-types
                    draggableAccessor: this.props.draggableAccessor,
                    // eslint-disable-next-line react/prop-types
                    resizableAccessor: this.props.resizableAccessor,
                    dragAndDropAction: this.state,
                },
            };
        }

        handleBeginAction = (event, action, direction) => {
            // eslint-disable-next-line react/no-unused-state
            this.setState({ event, action, direction });
        };

        handleInteractionStart = () => {
            if (this.state.interacting === false)
                this.setState({ interacting: true });
        };

        handleInteractionEnd = interactionInfo => {
            const { action, event } = this.state;

            if (!action) return;

            this.setState({
                action: null,
                event: null,
                interacting: false,
                // eslint-disable-next-line react/no-unused-state
                direction: null,
            });

            if (interactionInfo == null) return;

            interactionInfo.event = event;
            // eslint-disable-next-line react/prop-types
            if (action === 'move') this.props.onEventDrop(interactionInfo);
            // eslint-disable-next-line react/prop-types
            if (action === 'resize') this.props.onEventResize(interactionInfo);
        };

        render() {
            // eslint-disable-next-line react/prop-types
            const { selectable, ...props } = this.props;
            const { interacting } = this.state;
            delete props.onEventDrop;
            delete props.onEventResize;

            props.selectable = selectable ? 'ignoreEvents' : false;

            props.className = cn(
                props.className,
                'rbc-addons-dnd',
                !!interacting && 'rbc-addons-dnd-is-dragging',
            );

            return <Calendar {...props} components={this.components} />;
        }
    }

    return DragAndDropCalendar;
}
